import { useMechanicStore } from "@/abilities/mechanic/store/mechanic.store";
import { MechanicFilter, useMechanicHotspotQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { getDataQueryLanguages } from "@/shared/services/providers/language-provider";
import { get } from "@vueuse/core";
import { computed, Ref, ref, unref, watch } from "vue";

export interface MechanicHotspot {
    id: string;
    assemblyId?: string;
    parentAssemblyId?: string;
    articleId?: string;
    isAssembly?: boolean;
    reference2d?: string;
    reference3d?: string;
    articleNumber?: string;
    localizations?: {
        title?: string;
    };
    teasers?: {
        title?: string;
    };
    previewImage?: {
        url?: string;
    };
}

/**
 * Normalizes a raw GraphQL mechanic node into a MechanicHotspot.
 * Converts null values to undefined for consistent handling.
 */
export function toMechanicHotspot(node: { id: string } & Record<string, unknown>): MechanicHotspot {
    return {
        id: node.id,
        assemblyId: (node.assemblyId as string) ?? undefined,
        parentAssemblyId: (node.parentAssemblyId as string) ?? undefined,
        articleId: (node.articleId as string) ?? undefined,
        isAssembly: (node.isAssembly as boolean) ?? undefined,
        reference2d: (node.reference2d as string) ?? undefined,
        reference3d: (node.reference3d as string) ?? undefined,
        articleNumber: (node.articleNumber as string) ?? undefined,
        localizations: node.localizations
            ? { title: (node.localizations as Record<string, unknown>).title as string | undefined }
            : undefined,
        teasers: node.teasers
            ? { title: (node.teasers as Record<string, unknown>).title as string | undefined }
            : undefined,
        previewImage: node.previewImage
            ? { url: (node.previewImage as Record<string, unknown>).url as string | undefined }
            : undefined,
    };
}

export enum HotspotType {
    is3D = "is3D",
    is2D = "is2D",
}

/**
 * Composable to fetch and cache a single mechanic hotspot by its 2D/3D reference id.
 *
 * Resolution strategy:
 * 1. On id change, check the mechanic store cache (synchronous fast-path)
 * 2. If not cached, Apollo auto-fetches via reactive filter variables
 * 3. onHotspotResult validates the response still matches the current id before applying
 *
 * The query is disabled (via Apollo's `enabled` option) when:
 * - id is undefined/empty (nothing to look up)
 * - the id was already resolved from the store cache (avoid redundant network request)
 *
 * @param productId - Reactive product identifier used in the GraphQL filter
 * @param id - Reactive reference id (reference3d or reference2d) to look up
 * @param hotspotType - Whether to match by reference3d (is3D) or reference2d (is2D)
 */
export function useMechanicHotspots(productId: Ref<string>, id: Ref<string | undefined>, hotspotType: HotspotType) {
    const loading = ref(false);
    const error = ref(false);
    const mechanicStore = useMechanicStore();

    const mechanicHotspot = ref<MechanicHotspot | undefined>(undefined);

    // Whether the current id was resolved from cache (skip query)
    const cacheHit = ref(false);

    const filter = computed<MechanicFilter>(() => {
        const filterValue: MechanicFilter = {
            andGroup: [{ equals: { product: unref(productId) } }],
        };

        if (hotspotType === HotspotType.is3D && filterValue.andGroup) {
            filterValue.andGroup.push({
                equals: { reference3d: unref(id) },
            });
        }

        return filterValue;
    });

    // Query is enabled only when we have an id and no cache hit
    const queryEnabled = computed(() => !!get(id) && !cacheHit.value);

    const {
        loading: hotspotLoading,
        onResult: onHotspotResult,
        error: hotspotError,
    } = useMechanicHotspotQuery(
        () => ({
            filter: filter.value,
            acceptedLanguages: getDataQueryLanguages(),
            first: 1,
        }),
        () => ({
            enabled: queryEnabled.value,
        })
    );

    watch(hotspotLoading, (newValue) => {
        loading.value = newValue;
    });

    watch(hotspotError, (newValue) => {
        error.value = !!newValue;
        loading.value = false;
    });

    onHotspotResult((result) => {
        const currentReferenceId = get(id);
        const mechanicResult = result.data?.mechanics.mechanics.at(0)?.node;
        if (mechanicResult) {
            const hotspot = toMechanicHotspot(mechanicResult);
            // Only apply result if it still matches the current id
            const matchesCurrentId =
                hotspotType === HotspotType.is3D
                    ? hotspot.reference3d === currentReferenceId
                    : hotspot.reference2d === currentReferenceId;
            if (matchesCurrentId) {
                mechanicStore.addHotspots([hotspot]);
                mechanicHotspot.value = hotspot;
            }
        } else {
            mechanicHotspot.value = undefined;
        }
        loading.value = false;
    });

    // Watch id changes: resolve from cache synchronously when possible
    watch(
        id,
        (currentReferenceId) => {
            if (!currentReferenceId) {
                mechanicHotspot.value = undefined;
                cacheHit.value = false;
                loading.value = false;
                return;
            }

            const cachedHotspot =
                hotspotType === HotspotType.is3D
                    ? mechanicStore.getHotspotByReference3d(currentReferenceId)
                    : mechanicStore.getHotspotByReference2d(currentReferenceId);

            if (cachedHotspot) {
                mechanicHotspot.value = cachedHotspot;
                cacheHit.value = true;
                loading.value = false;
            } else {
                // Reset so the query fires for the new id
                mechanicHotspot.value = undefined;
                cacheHit.value = false;
            }
        },
        { immediate: true }
    );

    // Reactive computed for preview image URL
    const previewImageUrl = computed(() => mechanicHotspot.value?.previewImage?.url ?? "");

    return {
        error: computed(() => error.value),
        loading: computed(() => loading.value),
        mechanicHotspot: computed(() => mechanicHotspot.value),
        previewImageUrl,
    };
}
